import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Archive, Save, Sparkles, Wrench, X, Play } from "lucide-react";
import { useCurrentOrgId, useTestSkill, type SkillRow } from "@/hooks/useSkillOS";

const TASK_CLASSES = ["fast", "balanced", "reasoning", "retrieval", "vision"];
// Skill OS v20: enum binário-explícito de rollout. Sem rollout parcial.
const ROLLOUT_STATES = [
  { value: "off", label: "Off — não usada em produção" },
  { value: "shadow", label: "Shadow — executa mas não responde" },
  { value: "on", label: "On — produção total" },
];
const OUTPUT_FORMATS = [
  { value: "text", label: "Texto" },
  { value: "markdown", label: "Markdown" },
  { value: "json", label: "JSON estruturado" },
];
const INTENT_OPTIONS = [
  "question", "command", "complaint", "greeting", "task", "info",
  "analysis", "summary_request", "sales", "support", "poll",
  "welcome", "farewell", "escalation", "poll_request", "task_assign", "other",
];

const HARDCODED_NAMES = new Set([
  "summary", "faq-responder", "analyzer", "sales-assistant",
  "support", "poll", "conversational", "knowledge-sync",
  "sentiment", "moderator",
]);

export function SkillFormDialog({
  open, onOpenChange, skill,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  skill?: SkillRow | null;
}) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data: orgId } = useCurrentOrgId();
  const isEdit = !!skill;

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [taskClass, setTaskClass] = useState("balanced");
  const [active, setActive] = useState(true);
  const [executionType, setExecutionType] = useState<"hardcoded" | "prompt-based">("prompt-based");
  const [systemPrompt, setSystemPrompt] = useState("");
  const [promptFragment, setPromptFragment] = useState("");
  const [outputFormat, setOutputFormat] = useState("text");
  const [maxTokens, setMaxTokens] = useState(800);
  const [maxTokensInput, setMaxTokensInput] = useState("800");
  const [intentTriggers, setIntentTriggers] = useState<string[]>([]);
  const [tagsInput, setTagsInput] = useState("");
  const [priority, setPriority] = useState(0);
  const [priorityInput, setPriorityInput] = useState("0");
  const [rolloutState, setRolloutState] = useState<string>("off");

  const clamp = (raw: string, min: number, max: number, fallback: number) => {
    const n = Number(raw);
    if (!Number.isFinite(n)) return fallback;
    return Math.min(max, Math.max(min, Math.round(n)));
  };
  const [testMessage, setTestMessage] = useState("");
  const testMut = useTestSkill();

  useEffect(() => {
    if (skill) {
      setName(skill.name);
      setDescription(skill.description ?? "");
      const cfg = (skill.config ?? {}) as Record<string, unknown>;
      setTaskClass((cfg.task_class as string) || "balanced");
      setActive(skill.active);
      setExecutionType(((skill as any).execution_type as "hardcoded" | "prompt-based") || (HARDCODED_NAMES.has(skill.name) ? "hardcoded" : "prompt-based"));
      setSystemPrompt(((skill as any).system_prompt as string) ?? "");
      setPromptFragment(((skill as any).prompt_fragment as string) ?? "");
      setOutputFormat(((skill as any).output_format as string) || "text");
      setMaxTokens(((skill as any).max_tokens as number) || 800);
      setMaxTokensInput(String(((skill as any).max_tokens as number) || 800));
      setIntentTriggers(((skill as any).intent_triggers as string[]) ?? []);
      setTagsInput((((skill as any).tags as string[]) ?? []).join(", "));
      setPriority(((skill as any).priority as number) ?? 0);
      setPriorityInput(String(((skill as any).priority as number) ?? 0));
      // Skill OS v20: rollout binário-explícito. Valores legados degradam.
      const VALID_ROLLOUTS = new Set(["off", "shadow", "on", "deprecated"]);
      const raw = ((skill as any).rollout_state as string) || "off";
      setRolloutState(VALID_ROLLOUTS.has(raw) ? raw : "shadow");
    } else {
      setName(""); setDescription(""); setTaskClass("balanced"); setActive(true);
      setExecutionType("prompt-based"); setSystemPrompt(""); setPromptFragment("");
      setOutputFormat("text");
      setMaxTokens(800); setMaxTokensInput("800");
      setIntentTriggers([]);
      setTagsInput("");
      setPriority(0); setPriorityInput("0");
      setRolloutState("off");
    }
  }, [skill, open]);

  const isHardcoded = isEdit && HARDCODED_NAMES.has(skill!.name);
  const showPromptFields = executionType === "prompt-based";

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!orgId) throw new Error("Sem organização");
      const tags = tagsInput.split(",").map((t) => t.trim()).filter(Boolean);
      // Skill OS v20: rollout binário-explícito (off|shadow|on|deprecated).
      // Qualquer valor legado degrada para `shadow`.
      const safeRollout = ["off", "shadow", "on", "deprecated"].includes(rolloutState)
        ? rolloutState
        : "shadow";
      const payload = {
        description,
        config: { task_class: taskClass },
        active,
        execution_type: executionType,
        system_prompt: showPromptFields ? (systemPrompt || null) : null,
        prompt_fragment: showPromptFields ? (promptFragment || null) : null,
        output_format: outputFormat,
        max_tokens: maxTokens,
        intent_triggers: intentTriggers,
        tags,
        priority,
        rollout_state: safeRollout,
      };
      if (isEdit && skill) {
        const { error } = await supabase.from("skill_registry").update(payload).eq("id", skill.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("skill_registry").insert({ name, org_id: orgId, ...payload });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["skill-os", "skills"] });
      qc.invalidateQueries({ queryKey: ["skill-os", "routing-rules"] });
      toast({ title: isEdit ? "Skill atualizada" : "Skill criada" });
      onOpenChange(false);
    },
    onError: (e: Error) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  const archiveMut = useMutation({
    mutationFn: async () => {
      if (!skill) return;
      const { error } = await supabase.from("skill_registry")
        .update({ archived_at: new Date().toISOString(), active: false })
        .eq("id", skill.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["skill-os", "skills"] });
      toast({ title: "Skill arquivada" });
      onOpenChange(false);
    },
    onError: (e: Error) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  const toggleIntent = (intent: string) => {
    setIntentTriggers((prev) => prev.includes(intent) ? prev.filter((i) => i !== intent) : [...prev, intent]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {showPromptFields ? <Sparkles className="w-4 h-4 text-primary" /> : <Wrench className="w-4 h-4 text-muted-foreground" />}
            {isEdit ? `Editar: ${skill?.name}` : "Nova Skill"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Identidade */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">Nome (slug)</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} disabled={isEdit} placeholder="ex: pesquisa-mercado" />
            </div>
            <div>
              <label className="text-sm font-medium">Tipo de execução</label>
              <Select value={executionType} onValueChange={(v) => setExecutionType(v as "hardcoded" | "prompt-based")} disabled={isHardcoded}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="prompt-based">Prompt-based (custom)</SelectItem>
                  <SelectItem value="hardcoded" disabled={!isHardcoded}>Hardcoded (sistema)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Descrição</label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="O que esta skill faz?" />
          </div>

          {/* Config técnica */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-sm font-medium">Task Class</label>
              <Select value={taskClass} onValueChange={setTaskClass}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TASK_CLASSES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Output Format</label>
              <Select value={outputFormat} onValueChange={setOutputFormat}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{OUTPUT_FORMATS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Max Tokens <span className="text-xs text-muted-foreground font-normal">(64–4096)</span></label>
              <Input
                type="number"
                min={64}
                max={4096}
                value={maxTokensInput}
                onChange={(e) => setMaxTokensInput(e.target.value)}
                onBlur={() => {
                  const v = clamp(maxTokensInput, 64, 4096, 800);
                  setMaxTokens(v);
                  setMaxTokensInput(String(v));
                }}
              />
            </div>
          </div>

          {/* Prompts (condicional) */}
          {showPromptFields && (
            <>
              <div>
                <label className="text-sm font-medium">System Prompt</label>
                <Textarea
                  value={systemPrompt}
                  onChange={(e) => setSystemPrompt(e.target.value)}
                  rows={4}
                  placeholder="Você é um especialista em… Responda em PT-BR…"
                />
                <p className="text-xs text-muted-foreground mt-1">Se vazio, será usado o prompt padrão da Syra + descrição/fragment.</p>
              </div>
              <div>
                <label className="text-sm font-medium">Prompt Fragment (instrução adicional)</label>
                <Textarea
                  value={promptFragment}
                  onChange={(e) => setPromptFragment(e.target.value)}
                  rows={3}
                  placeholder="Sempre liste 3 pontos. Use bullets. Termine com pergunta de follow-up."
                />
              </div>
            </>
          )}

          {/* Roteamento */}
          <div>
            <label className="text-sm font-medium">Intent Triggers</label>
            <p className="text-xs text-muted-foreground mb-2">
              Esta skill será disparada quando uma destas intenções for detectada (cria/atualiza routing rules automaticamente).
            </p>
            <div className="flex flex-wrap gap-1.5">
              {INTENT_OPTIONS.map((intent) => (
                <Badge
                  key={intent}
                  variant={intentTriggers.includes(intent) ? "default" : "outline"}
                  className="cursor-pointer text-xs"
                  onClick={() => toggleIntent(intent)}
                >
                  {intent}
                  {intentTriggers.includes(intent) && <X className="w-3 h-3 ml-1" />}
                </Badge>
              ))}
            </div>
          </div>

          {/* Metadados */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">Tags (vírgula)</label>
              <Input value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} placeholder="vendas, premium, beta" />
            </div>
            <div>
              <label className="text-sm font-medium">Prioridade <span className="text-xs text-muted-foreground font-normal">(0–100)</span></label>
              <Input
                type="number"
                min={0}
                max={100}
                value={priorityInput}
                onChange={(e) => setPriorityInput(e.target.value)}
                onBlur={() => {
                  const v = clamp(priorityInput, 0, 100, 0);
                  setPriority(v);
                  setPriorityInput(String(v));
                }}
              />
            </div>
          </div>

          {/* Rollout governance */}
          <div className="rounded-md border border-border p-3 space-y-3">
            <p className="text-sm font-medium">Rollout (governança de produção)</p>
            <div>
              <label className="text-xs text-muted-foreground">Estado</label>
              <Select value={rolloutState} onValueChange={setRolloutState}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{ROLLOUT_STATES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground">Pipeline só dispara skill em estado <code>on</code>.</p>
          </div>

          {/* Status */}
          <div className="flex items-center justify-between rounded-md border border-border p-3">
            <div>
              <p className="text-sm font-medium">Skill ativa</p>
              <p className="text-xs text-muted-foreground">Skills inativas não são roteadas pelo pipeline.</p>
            </div>
            <Switch checked={active} onCheckedChange={setActive} />
          </div>

          {/* Test preview (only when editing existing skill) */}
          {isEdit && skill && (
            <div className="rounded-md border border-border p-3 space-y-2 bg-muted/30">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium flex items-center gap-1.5"><Play className="w-3.5 h-3.5" /> Test preview</p>
                <Badge variant="outline" className="text-xs">test_mode (não envia ao grupo)</Badge>
              </div>
              <Textarea
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                rows={2}
                placeholder="Digite uma mensagem de teste para esta skill…"
                className="text-sm"
              />
              <Button
                size="sm"
                variant="secondary"
                disabled={!testMessage.trim() || !orgId || testMut.isPending}
                onClick={() => testMut.mutate({ skill_name: skill.name, message: testMessage, org_id: orgId! })}
              >
                {testMut.isPending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}
                Testar agora
              </Button>
              {testMut.data && (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Badge variant="outline" className="text-[10px]">{testMut.data.dispatch_type}</Badge>
                    <span>{testMut.data.execution_ms}ms</span>
                  </div>
                  <pre className="text-xs whitespace-pre-wrap bg-background border border-border rounded p-2 max-h-48 overflow-auto">{testMut.data.output}</pre>
                </div>
              )}
              {testMut.error && (
                <p className="text-xs text-destructive">{(testMut.error as Error).message}</p>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          {isEdit && !isHardcoded && (
            <Button variant="outline" onClick={() => archiveMut.mutate()} disabled={archiveMut.isPending}>
              <Archive className="w-4 h-4 mr-1" /> Arquivar
            </Button>
          )}
          <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending || !name.trim()}>
            {saveMut.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
